import { CheckIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { Icon } from '#common/components/Icon';
import { type TranslateFunc } from '#common/i18n/edit-mode.tsx';
import { getAcceptedCategories } from '#common/utils/consent-handling.tsx';
import { type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';
import { getAllPublicEnv } from '#speaker-shop/utils/public-env.tsx';
import { Button } from './Button';

interface ConsentOverlayProps {
    t: TranslateFunc;
    appearance?: 'legacy' | 'unified';
    theme?: CoreTheme;
}

function acceptFunctionalCookies() {
    try {
        const { existingCategories, existingGoogleConsentMap } = getAcceptedCategories();

        // Merge with functionality category
        const categories = Array.from(new Set([ ...existingCategories, 'functionality' ]));

        // Update Google consent map to include functionality storage
        const googleconsentmap = {
            ...existingGoogleConsentMap,
            functionality_storage: 'functionality',
            personalization_storage: 'functionality',
            security_storage: 'functionality'
        };

        const consentData = {
            googleconsentmap,
            action: 'accept',
            consenttime: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
            categories: JSON.stringify(categories)
        };

        // Write the consent cookie
        const expires = new Date();
        expires.setMonth(expires.getMonth() + 1);

        const env = getAllPublicEnv();

        document.cookie = `CookieScriptConsent=${encodeURIComponent(JSON.stringify(consentData))}; path=/; domain=${
            '.' + env.BASE_HOST
        }; expires=${expires.toUTCString()}; ${window.location.protocol === 'https:' ? 'secure;' : ''}`;
        window.location.reload();
    } catch (error) {
        console.error('Failed to set consent cookie:', error);
    }
}

export function ConsentOverlay({
    t,
    appearance = 'legacy',
    theme = 'dark',
}: ConsentOverlayProps) {
    const unified = appearance === 'unified';
    return (
        <div
            className={clsx(
                'h-full backdrop-blur-xs flex items-center justify-center p-2 md:p-6 transition-opacity duration-200 z-20',
                unified ? 'core-booking-theme core-generation-overlay' : 'bg-black/60'
            )}
            data-theme={unified ? theme : undefined}
        >
            <div
                className={clsx(
                    'max-w-md w-full mx-auto',
                    unified ? 'core-generation-panel p-5 sm:p-6' : 'bg-white rounded-lg shadow-2xl p-4'
                )}
            >
                <div className='text-center'>
                    <div
                        className={clsx(
                            'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4',
                            unified ? 'bg-[color-mix(in_srgb,var(--core-blue)_16%,transparent)]' : 'bg-blue-100'
                        )}
                    >
                        <Icon
                            icon='heroicons--shield-check'
                            className={clsx('text-3xl', unified ? 'text-[var(--core-blue)]' : 'text-blue-600')}
                        />
                    </div>
                    <h3
                        className={clsx(
                            'text-2xl font-bold mb-3',
                            unified ? 'text-[var(--core-text)]' : 'text-gray-900'
                        )}
                    >
                        {t('consent-required-title')}
                    </h3>
                    <p className={clsx('mb-6', unified ? 'core-booking-muted' : 'text-gray-600')}>
                        {t('consent-required-message')}
                    </p>
                    <div className='space-y-3 justify-center items-center'>
                        {unified
                            ? (
                                <button
                                    className='core-primary-button w-full'
                                    type='button'
                                    onClick={acceptFunctionalCookies}
                                >
                                    <CheckIcon className='size-5' /> {t('accept-cookies')}
                                </button>
                            )
                            : (
                                <Button
                                    size='big'
                                    color='blue'
                                    className='w-full items-center'
                                    type='button'
                                    onClick={acceptFunctionalCookies}
                                >
                                    <CheckIcon className='h-7 w-7 mr-2' /> {t('accept-cookies')}
                                </Button>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
}
